import React, { useId, useMemo } from 'react';

interface EngineeringDimensionCalloutsProps {
  readonly width?: number;
  readonly height?: number;
  readonly unit: string;
  readonly precision?: number;
  readonly horizontalLabel?: string | null;
  readonly verticalLabel?: string | null;
  readonly horizontalPlacement?: 'top' | 'bottom';
  readonly verticalPlacement?: 'left' | 'right';
  readonly horizontalDimensionYPx?: number;
  readonly verticalDimensionXPx?: number;
  readonly verticalExtendPx?: number;
  readonly verticalLabelOffsetPx?: number;
  readonly bounds?: {
    left: number;
    right: number;
    top: number;
    bottom: number;
    viewportWidth: number;
    viewportHeight: number;
  } | null;
}

/**
 * Renders blueprint-style dimension callouts for the top and right edges of the preview canvas.
 */
const EngineeringDimensionCallouts: React.FC<EngineeringDimensionCalloutsProps> = ({
  width,
  height,
  unit,
  precision = 2,
  horizontalLabel,
  verticalLabel,
  horizontalPlacement = 'top',
  verticalPlacement = 'right',
  horizontalDimensionYPx,
  verticalDimensionXPx,
  verticalExtendPx,
  verticalLabelOffsetPx,
  bounds,
}) => {
  const id = useId();

  const resolvedHorizontalLabel = useMemo(() => {
    if (horizontalLabel) {
      return horizontalLabel;
    }
    if (typeof width !== 'number' || !Number.isFinite(width) || width <= 0) {
      return null;
    }
    return `${width.toFixed(precision)} ${unit}`;
  }, [horizontalLabel, precision, unit, width]);

  const resolvedVerticalLabel = useMemo(() => {
    if (verticalLabel) {
      return verticalLabel;
    }
    if (typeof height !== 'number' || !Number.isFinite(height) || height <= 0) {
      return null;
    }
    return `${height.toFixed(precision)} ${unit}`;
  }, [height, precision, unit, verticalLabel]);

  if (!resolvedHorizontalLabel && !resolvedVerticalLabel) {
    return null;
  }

  const stroke = '#5f6368';
  const fallbackWidth = 100;
  const fallbackHeight = 100;
  const viewBoxWidth = bounds?.viewportWidth ?? fallbackWidth;
  const viewBoxHeight = bounds?.viewportHeight ?? fallbackHeight;
  const usableBounds = bounds
    ? {
        left: bounds.left,
        right: bounds.right,
        top: bounds.top,
        bottom: bounds.bottom,
      }
    : {
        left: viewBoxWidth * 0.15,
        right: viewBoxWidth * 0.85,
        top: viewBoxHeight * 0.2,
        bottom: viewBoxHeight * 0.8,
      };

  const gapFromObject = 12;
  const extensionOvershoot = 8;

  const defaultHorizontalDimensionY = horizontalPlacement === 'bottom'
    ? Math.min(usableBounds.bottom + gapFromObject, viewBoxHeight)
    : Math.max(usableBounds.top - gapFromObject, 0);
  const horizontalDimensionY = typeof horizontalDimensionYPx === 'number' && Number.isFinite(horizontalDimensionYPx)
    ? Math.min(Math.max(horizontalDimensionYPx, 0), viewBoxHeight)
    : defaultHorizontalDimensionY;
  const horizontalExtensionEnd = horizontalPlacement === 'bottom'
    ? Math.min(horizontalDimensionY + extensionOvershoot, viewBoxHeight)
    : Math.max(horizontalDimensionY - extensionOvershoot, 0);
  const defaultVerticalDimensionX = verticalPlacement === 'left'
    ? Math.max(usableBounds.left - gapFromObject, 0)
    : Math.min(usableBounds.right + gapFromObject, viewBoxWidth);
  const verticalDimensionX = typeof verticalDimensionXPx === 'number' && Number.isFinite(verticalDimensionXPx)
    ? Math.min(Math.max(verticalDimensionXPx, 0), viewBoxWidth)
    : defaultVerticalDimensionX;
  const verticalExtensionEnd = verticalPlacement === 'left'
    ? Math.max(verticalDimensionX - extensionOvershoot, 0)
    : Math.min(verticalDimensionX + extensionOvershoot, viewBoxWidth);

  const horizontalTextY = horizontalPlacement === 'bottom'
    ? Math.min(horizontalDimensionY + 6, viewBoxHeight - 6)
    : Math.max(horizontalDimensionY - 6, 6);
  const verticalTextX = verticalPlacement === 'left'
    ? Math.max(verticalDimensionX - 8, 4)
    : Math.min(verticalDimensionX + 8, viewBoxWidth - 4);
  const verticalFontSize = 12;
  const approxCharWidth = verticalFontSize * 0.6;
  const verticalLabelWidth = resolvedVerticalLabel ? resolvedVerticalLabel.length * approxCharWidth : 0;
  const continuationPadding = verticalFontSize * 0.6;
  const continuationMin = 45;
  const continuationLength = Math.max(continuationMin, verticalLabelWidth + continuationPadding);
  const defaultVerticalLabelOffset = Math.max(verticalFontSize, verticalLabelWidth * 0.25);
  const verticalSpan = Math.max(0, usableBounds.bottom - usableBounds.top);
  const canFitLabelInSpan = verticalLabelWidth > 0 && verticalSpan >= verticalLabelWidth + verticalFontSize * 0.8;
  const resolvedVerticalExtend = canFitLabelInSpan
    ? 0
    : (typeof verticalExtendPx === 'number' && verticalExtendPx > 0
      ? verticalExtendPx
      : Math.max(defaultVerticalLabelOffset + continuationLength, verticalFontSize * 2.5));
  const verticalLineBottom = Math.min(usableBounds.bottom + resolvedVerticalExtend, viewBoxHeight);
  const verticalLabelOffset = typeof verticalLabelOffsetPx === 'number' && verticalLabelOffsetPx > 0
    ? verticalLabelOffsetPx
    : null;
  const verticalTextCenterY = resolvedVerticalExtend > 0
    ? (verticalLabelOffset != null
      ? Math.min(Math.max(usableBounds.bottom + verticalLabelOffset, usableBounds.bottom + 2), verticalLineBottom - 4)
      : (usableBounds.bottom + verticalLineBottom) / 2)
    : (usableBounds.top + usableBounds.bottom) / 2;
  const horizontalFontSize = 12;
  const horizontalLabelWidth = resolvedHorizontalLabel ? resolvedHorizontalLabel.length * approxCharWidth : 0;
  const horizontalSpan = Math.max(0, usableBounds.right - usableBounds.left);
  const canFitHorizontalLabel = horizontalLabelWidth > 0 && horizontalSpan >= horizontalLabelWidth + horizontalFontSize * 0.8;
  const horizontalContinuationMin = 45;
  const horizontalContinuationLength = Math.max(horizontalContinuationMin, horizontalLabelWidth + horizontalFontSize * 0.6);
  const horizontalLineRight = canFitHorizontalLabel
    ? usableBounds.right
    : Math.min(usableBounds.right + horizontalContinuationLength, viewBoxWidth);
  const horizontalTextCenterX = canFitHorizontalLabel
    ? (usableBounds.left + usableBounds.right) / 2
    : (usableBounds.right + horizontalLineRight) / 2;

  return (
    <svg
      className="pointer-events-none absolute inset-0"
      viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
      preserveAspectRatio="none"
    >
      <defs>
        <marker
          id={`${id}-arrow`}
          markerWidth="6"
          markerHeight="6"
          refX="3"
          refY="3"
          orient="auto-start-reverse"
          markerUnits="strokeWidth"
        >
          <path
            d="M0,0 L6,3 L0,6"
            fill="none"
            stroke={stroke}
            strokeWidth={0.8}
            vectorEffect="non-scaling-stroke"
          />
        </marker>
      </defs>

      {resolvedHorizontalLabel ? (
        <>
          {horizontalPlacement === 'bottom' ? (
            <>
              <path
                d={`M${usableBounds.left},${usableBounds.bottom} V${horizontalExtensionEnd}`}
                stroke={stroke}
                strokeWidth={0.8}
                vectorEffect="non-scaling-stroke"
              />
              <path
                d={`M${usableBounds.right},${usableBounds.bottom} V${horizontalExtensionEnd}`}
                stroke={stroke}
                strokeWidth={0.8}
                vectorEffect="non-scaling-stroke"
              />
            </>
          ) : (
            <>
              <path
                d={`M${usableBounds.left},${usableBounds.top} V${horizontalExtensionEnd}`}
                stroke={stroke}
                strokeWidth={0.8}
                vectorEffect="non-scaling-stroke"
              />
              <path
                d={`M${usableBounds.right},${usableBounds.top} V${horizontalExtensionEnd}`}
                stroke={stroke}
                strokeWidth={0.8}
                vectorEffect="non-scaling-stroke"
              />
            </>
          )}
          <path
            d={`M${usableBounds.left},${horizontalDimensionY} H${usableBounds.right}`}
            stroke={stroke}
            strokeWidth={0.8}
            markerStart={`url(#${id}-arrow)`}
            markerEnd={`url(#${id}-arrow)`}
            vectorEffect="non-scaling-stroke"
          />
          {!canFitHorizontalLabel ? (
            <path
              d={`M${usableBounds.right},${horizontalDimensionY} H${horizontalLineRight}`}
              stroke={stroke}
              strokeWidth={0.8}
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
          <text
            x={horizontalTextCenterX}
            y={horizontalTextY}
            textAnchor="middle"
            fill={stroke}
            fontSize={horizontalFontSize}
            fontFamily="'IBM Plex Mono', 'Menlo', 'Consolas', 'Liberation Mono', sans-serif"
            stroke="#f9f9f9"
            strokeWidth={0.3}
            paintOrder="stroke"
            dominantBaseline="middle"
          >
            {resolvedHorizontalLabel}
          </text>
        </>
      ) : null}

      {resolvedVerticalLabel ? (
        <>
          {verticalPlacement === 'left' ? (
            <>
              <path
                d={`M${usableBounds.left},${usableBounds.top} H${verticalExtensionEnd}`}
                stroke={stroke}
                strokeWidth={0.8}
                vectorEffect="non-scaling-stroke"
              />
              <path
                d={`M${usableBounds.left},${usableBounds.bottom} H${verticalExtensionEnd}`}
                stroke={stroke}
                strokeWidth={0.8}
                vectorEffect="non-scaling-stroke"
              />
            </>
          ) : (
            <>
              <path
                d={`M${usableBounds.right},${usableBounds.top} H${verticalExtensionEnd}`}
                stroke={stroke}
                strokeWidth={0.8}
                vectorEffect="non-scaling-stroke"
              />
              <path
                d={`M${usableBounds.right},${usableBounds.bottom} H${verticalExtensionEnd}`}
                stroke={stroke}
                strokeWidth={0.8}
                vectorEffect="non-scaling-stroke"
              />
            </>
          )}
          <path
            d={`M${verticalDimensionX},${usableBounds.top} V${usableBounds.bottom}`}
            stroke={stroke}
            strokeWidth={0.8}
            markerStart={`url(#${id}-arrow)`}
            markerEnd={`url(#${id}-arrow)`}
            vectorEffect="non-scaling-stroke"
          />
          {resolvedVerticalExtend > 0 ? (
            <path
              d={`M${verticalDimensionX},${usableBounds.bottom} V${verticalLineBottom}`}
              stroke={stroke}
              strokeWidth={0.8}
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
          <text
            x={verticalTextX}
            y={verticalTextCenterY}
            textAnchor="middle"
            fill={stroke}
            fontSize={verticalFontSize}
            fontFamily="'IBM Plex Mono', 'Menlo', 'Consolas', 'Liberation Mono', sans-serif"
            stroke="#f9f9f9"
            strokeWidth={0.3}
            paintOrder="stroke"
            transform={`rotate(-90 ${verticalTextX} ${verticalTextCenterY})`}
            dominantBaseline="middle"
          >
            {resolvedVerticalLabel}
          </text>
        </>
      ) : null}
    </svg>
  );
};

export default EngineeringDimensionCallouts;
